import * as React from 'react';
import { useEffect } from 'react';
import type { InjectedRouter } from 'react-router';

import connectToStores from 'js/lib/connectToStores';
import useRouter from 'js/lib/useRouter';

import { useCourseNavigationContext } from 'bundles/course-v2/providers/CourseNavigationProvider';
import CourseNextStepProvider from 'bundles/course-v2/providers/CourseNextStepProvider';
import { useIsWeekToModuleEnabledForCourse } from 'bundles/custom-labels/utils/featureFlags';

type Props = {
  courseId: string;
};

const Redirect: React.FC<{ to: string; router: InjectedRouter }> = ({ to, router }) => {
  useEffect(() => router.replace(to), [router, to]);
  return null;
};

export const CourseHomeRefreshRedirect = ({ courseId }: Props) => {
  const router = useRouter();
  const courseSlug = router.params.courseSlug;
  // I see a comment below saying the connect to store call that retrieves the courseId is always undefined, this may be causing a failed query everytime the page redirects
  // since its entering an undefined courseId so testing that theory out
  const isWeekToModuleEnabledForCourse = useIsWeekToModuleEnabledForCourse(courseId);

  const { currentModule, loading } = useCourseNavigationContext();

  // We use the CourseNavigation currentWeek (currentModule) value as a backup in case
  // the current learner does not have a courseMaterialNextStep
  return (
    <CourseNextStepProvider courseSlug={courseSlug}>
      {({ courseNextStep }) => {
        const nextStep = courseNextStep?.nextStep;

        let currentWeekNumber = 1;

        // don't redirect until we have data from both providers
        if (loading) return null;

        if (nextStep?.typeName === 'courseMaterialNextStep') {
          currentWeekNumber = nextStep?.definition.currentWeekNumberByProgress;
        } else if (currentModule) {
          currentWeekNumber = currentModule;
        }

        if (isWeekToModuleEnabledForCourse) {
          return <Redirect router={router} to={`/learn/${courseSlug}/home/module/${currentWeekNumber}`} />;
        } else {
          return <Redirect router={router} to={`/learn/${courseSlug}/home/week/${currentWeekNumber}`} />;
        }
      }}
    </CourseNextStepProvider>
  );
};

export default connectToStores<Props, {}>(['CourseStore'], (CourseStore) => {
  return {
    /**
     * TODO: this always return undefined should it just be deleted?
     */
    courseId: CourseStore.getCourseId(),
  };
})(CourseHomeRefreshRedirect);
